import { deltaVWordHtml } from '../engine/delta-v-word';
import {
  clearPendingOrder,
  esc,
  formatMoney,
  getCatalogue,
  pollDepositStatus,
  readReturnedOrderId,
  toPaymentError,
} from '../core/payments';
import type { DepositOrder } from '../core/payments';
import { fetchStoreBalance } from './shop/shop-api';
import { AMBIENT, mountAmbientBackground } from '../engine/ambient-background';
import { t } from '../engine/i18n/i18n';

/**
 * Gateway return page: `/payment-result?order=<orderId>`.
 *
 * Landing here proves nothing about the payment: the gateway redirect is
 * client-controlled, and the credit happens server-side on the signed webhook.
 * So this screen only ever DISPLAYS the order status the server reports, by
 * polling it. It never credits anything and never trusts query parameters
 * beyond the order id (which the server re-checks belongs to the player).
 */

type View =
  | { kind: 'loading'; attempt: number }
  | { kind: 'missing' }
  | { kind: 'success'; order: DepositOrder; deltaV: number | null; balance: number | null }
  | { kind: 'failed'; order: DepositOrder }
  | { kind: 'review'; order: DepositOrder }
  | { kind: 'timeout'; order: DepositOrder }
  | { kind: 'error'; message: string };

const POLL_INTERVAL_MS = 2000;
const POLL_TRIES = 15;
const RECHECK_TRIES = 5;

export function renderPaymentResult(root: HTMLElement, onReturn: () => void): () => void {
  root.innerHTML = `
    <div class="relative w-full min-h-dvh bg-bg">
      <div data-ambient class="absolute inset-0 overflow-hidden pointer-events-none"></div>
      <main class="relative min-h-dvh flex items-center justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div data-result class="glass-panel w-full max-w-md p-6 flex flex-col items-center text-center gap-4" aria-live="polite"></div>
      </main>
    </div>`;
  mountAmbientBackground(root.querySelector('[data-ambient]') as HTMLElement, AMBIENT.shop);
  const panel = root.querySelector('[data-result]') as HTMLElement;

  let disposed = false;
  let abort = new AbortController();
  let orderId: string | null = readReturnedOrderId();

  const btn = (action: string, label: string, primary: boolean) => `
    <button type="button" data-action="${action}"
      class="font-button w-full min-h-11 px-6 text-sm rounded transition-colors duration-[120ms] touch-manipulation ${
        primary ? 'bg-gold text-bg hover:bg-gold-bright' : 'border border-border text-text hover:border-gold/40'
      }">${esc(label)}</button>`;

  function paint(view: View): void {
    switch (view.kind) {
      case 'loading':
        panel.innerHTML = `
          <div class="text-gold motion-safe:animate-pulse" aria-hidden="true">${deltaVWordHtml('lg')}</div>
          <h1 class="font-heading text-lg text-gold">${t('paymentResult.loading.title')}</h1>
          <p class="text-sm text-text-dim leading-relaxed" role="status">${t('paymentResult.loading.body')}</p>
          <p class="text-[10px] text-text-dim tabular-nums">${t('paymentResult.loading.check', { attempt: view.attempt, total: POLL_TRIES })}</p>`;
        break;
      case 'missing':
        panel.innerHTML = `
          <h1 class="font-heading text-lg text-gold">${t('paymentResult.missing.title')}</h1>
          <p class="text-sm text-text-dim leading-relaxed">${t('paymentResult.missing.body')}</p>
          ${btn('return', t('paymentResult.return'), true)}`;
        break;
      case 'success': {
        const { order, deltaV, balance } = view;
        panel.innerHTML = `
          <div class="text-success" aria-hidden="true">${deltaVWordHtml('lg')}</div>
          <h1 class="font-heading text-lg text-success" data-result-title>${t('paymentResult.success.title')}</h1>
          ${deltaV !== null
            ? `<div><div class="font-heading text-3xl text-gold tabular-nums" data-result-deltav>+${Math.floor(deltaV).toLocaleString('en-US')}</div><div class="text-[10px] text-text-dim uppercase tracking-wide">${t('term.deltaV')}</div></div>`
            : ''}
          <dl class="w-full flex flex-col gap-1 text-sm">
            <div class="flex justify-between gap-3"><dt class="text-text-dim">${t('paymentResult.success.paid')}</dt><dd class="text-text tabular-nums">${esc(formatMoney(order.amountCents, order.currency))}</dd></div>
            ${balance !== null ? `<div class="flex justify-between gap-3"><dt class="text-text-dim">${t('paymentResult.success.newBalance')}</dt><dd class="text-gold tabular-nums">${t('paymentResult.success.balanceValue', { amount: Math.floor(balance).toLocaleString('en-US') })}</dd></div>` : ''}
          </dl>
          ${order.credited ? '' : `<p class="text-xs text-text-dim leading-relaxed">${t('paymentResult.success.pending')}</p>`}
          ${btn('return', t('paymentResult.return'), true)}`;
        break;
      }
      case 'failed':
        panel.innerHTML = `
          <h1 class="font-heading text-lg text-error" data-result-title>${view.order.status === 'EXPIRED' ? t('paymentResult.failed.titleExpired') : t('paymentResult.failed.titleFailed')}</h1>
          <p class="text-sm text-text-dim leading-relaxed" role="alert">${
            view.order.status === 'EXPIRED'
              ? t('paymentResult.failed.bodyExpired')
              : t('paymentResult.failed.bodyFailed')}</p>
          ${btn('retry', t('paymentResult.tryAgain'), true)}
          ${btn('return', t('paymentResult.return'), false)}`;
        break;
      case 'review':
        panel.innerHTML = `
          <h1 class="font-heading text-lg text-gold" data-result-title>${t('paymentResult.review.title')}</h1>
          <p class="text-sm text-text-dim leading-relaxed">${t('paymentResult.review.body')}</p>
          ${btn('return', t('paymentResult.return'), true)}`;
        break;
      case 'timeout':
        panel.innerHTML = `
          <h1 class="font-heading text-lg text-gold" data-result-title>${t('paymentResult.timeout.title')}</h1>
          <p class="text-sm text-text-dim leading-relaxed">${t('paymentResult.timeout.body')}</p>
          ${btn('recheck', t('paymentResult.checkAgain'), true)}
          ${btn('return', t('paymentResult.return'), false)}`;
        break;
      case 'error':
        panel.innerHTML = `
          <h1 class="font-heading text-lg text-error" data-result-title>${t('paymentResult.error.title')}</h1>
          <p class="text-sm text-text-dim leading-relaxed" role="alert">${esc(view.message)}</p>
          ${btn('recheck', t('paymentResult.tryAgain'), true)}
          ${btn('return', t('paymentResult.return'), false)}`;
        break;
    }
  }

  /** Best-effort extras for the success card: never block or fail the result on them. */
  async function successExtras(order: DepositOrder): Promise<{ deltaV: number | null; balance: number | null }> {
    const [cat, bal] = await Promise.allSettled([order.productId ? getCatalogue() : Promise.reject(), fetchStoreBalance()]);
    let deltaV: number | null = null;
    if (cat.status === 'fulfilled') {
      const v = cat.value.products.find((p) => p.id === order.productId)?.display?.deltaV;
      if (typeof v === 'number') deltaV = v;
    }
    return { deltaV, balance: bal.status === 'fulfilled' ? bal.value.premium : null };
  }

  async function check(tries: number): Promise<void> {
    if (!orderId) { paint({ kind: 'missing' }); return; }
    abort.abort();
    abort = new AbortController();
    const { signal } = abort;
    let attempt = 0;
    paint({ kind: 'loading', attempt: 1 });
    try {
      const order = await pollDepositStatus(orderId, {
        intervalMs: POLL_INTERVAL_MS,
        maxTries: tries,
        signal,
        onUpdate: () => {
          attempt += 1;
          if (!disposed && !signal.aborted) paint({ kind: 'loading', attempt: Math.min(attempt + 1, POLL_TRIES) });
        },
      });
      if (disposed || signal.aborted) return;
      switch (order.status) {
        case 'APPROVED':
        case 'PARTIALLY_REFUNDED':
        case 'REFUNDED': {
          const extras = await successExtras(order);
          if (disposed || signal.aborted) return;
          paint({ kind: 'success', order, ...extras });
          break;
        }
        case 'DECLINED':
        case 'ERROR':
        case 'EXPIRED':
          paint({ kind: 'failed', order });
          break;
        case 'MISMATCH':
          paint({ kind: 'review', order });
          break;
        default:
          paint({ kind: 'timeout', order });
      }
    } catch (err) {
      if (disposed || signal.aborted) return;
      paint({ kind: 'error', message: toPaymentError(err).message });
    }
  }

  function onClick(e: Event): void {
    const action = ((e.target as HTMLElement).closest('[data-action]') as HTMLElement | null)?.dataset.action;
    if (action === 'return') {
      clearPendingOrder();
      onReturn();
    } else if (action === 'retry') {
      // A fresh attempt starts from the shop's Delta-V shelf, not this order.
      clearPendingOrder();
      onReturn();
    } else if (action === 'recheck') {
      void check(RECHECK_TRIES);
    }
  }
  panel.addEventListener('click', onClick);

  void check(POLL_TRIES);

  return () => {
    disposed = true;
    abort.abort();
    panel.removeEventListener('click', onClick);
  };
}
